import { Gateway } from '@bolteu/react-api-gateway/es';

import { TranslatableField } from '../types';

export interface GetAvailableRecommendationsAndFocusesRequest {
  provider_id: number;
  ignore_focuses?: boolean;
}

export interface GetAvailableRecommendationsAndFocusesResponse {
  recommendations: Focus[];
  focuses: Focus[];
  best_user_recommendations: Focus[];
}

export interface Focus {
  key: string;
  weekly_spend_value: { currency: string; value: number };
  weekly_impact_value: { currency: string; value: number };
  filters: GetWeeklyForecastRequestFilters;
}

export interface GetWeeklyForecastRequestFilters {
  type_of_discount: TypeOfDiscount;
  discount_target: DiscountTarget;
  discount_amount?: number;
}

export interface WeeklyForecastFiltersForUpliftStats {
  type_of_discount?: TypeOfDiscount;
  discount_target?: DiscountTarget;
  discount_amount?: number;
}

export enum TypeOfDiscount {
  FREE_DELIVERY = 'type_of_discount_free_delivery',
  FREE_BASE_DELIVERY = 'type_of_discount_free_base_delivery',
  FULL_MENU = 'type_of_discount_full_menu',
  DOUBLE_DEAL = 'type_of_discount_double_deal',
}

export enum DiscountTarget {
  ALL_CUSTOMER = 'discount_targeting_all_customers',
  NEW_CUSTOMER = 'discount_targeting_new_customers',
  RETURNING_CUSTOMER = 'discount_targeting_returning_customers',
  CHURNED_CUSTOMER = 'discount_targeting_churned_customers',
  BEST_CUSTOMER = 'discount_targeting_best_customers',
}

export enum AnalyticsEventName {
  PROMOTIONS_SIGN_UP = 'promotion_sign_up',
  PROMOTION_SECTION_VISITED = 'promotion_section_visited',
  PROMOTION_CREATION_PAGE_VISITED = 'promotion_creation_page_visited',
}

interface AnalyticsEventBase {
  type: AnalyticsEventName;
}

interface SignUpAnalyticsEvent extends AnalyticsEventBase {
  type: AnalyticsEventName.PROMOTIONS_SIGN_UP;
  promotion_id: number;
}

interface PromotionSectionVisitedAnalyticsEvent extends AnalyticsEventBase {
  type: AnalyticsEventName.PROMOTION_SECTION_VISITED;
}

interface PromotionCreationPageVisitedAnalyticsEvent extends AnalyticsEventBase {
  type: AnalyticsEventName.PROMOTION_CREATION_PAGE_VISITED;
}

type AnalyticsEvent =
  | SignUpAnalyticsEvent
  | PromotionSectionVisitedAnalyticsEvent
  | PromotionCreationPageVisitedAnalyticsEvent;

export interface StoreAnalyticsEventRequest {
  event: AnalyticsEvent;
}

export type GetTestFlagsRequest = {
  provider_id: number;
};

export interface GetTestFlagsResponse {
  should_use_new_interface: boolean;
}

export interface LocalisedString {
  locale: string;
  value: string;
}

export interface GetProviderAvailablePromotionsRequest {
  provider_id: number;
}

export interface GetProviderAvailablePromotionsResponse {
  campaigns: AvailableSelfServiceCampaign[];
}

export interface AvailableSelfServiceCampaign {
  title: LocalisedString;
  description: LocalisedString;
  trait: string;
  available_between: SelfServiceCampaignAvailabilityInterval;
}

export interface SelfServiceCampaignAvailabilityInterval {
  from: number;
  to: number;
}

export interface GetAvailablePromotionsOptionsTreeRequest {
  provider_id: number;
}

export interface BestUserPromotionPermissions {
  is_allowed_to_see_best_user_promotion: boolean;
  is_allowed_to_create_best_user_promotion: boolean;
}

export interface SelectedItemsCityConfig {
  min_items_count?: number;
  total_items_min_price?: number;
}

export interface GetAvailablePromotionsOptionsTreeResponse {
  options: Section;
  is_food_promotion_banned: boolean;
  banned_until?: number;
  best_user_promotion_permissions: BestUserPromotionPermissions;
  selected_items_config: SelectedItemsCityConfig;
  exclusion_tags: string[];
}

export interface Section {
  key: PromotionSectionKeys;
  input_type:
    | CreatePromotionTemplateInputType.SELECT
    | CreatePromotionTemplateInputType.DROPDOWN
    | CreatePromotionTemplateInputType.SELECT_DISABLED;
  name: string;
  description: string;
  choices: Choice[];
}

export enum DeliveryBonusType {
  PERCENTAGE_OF_BASE_DELIVERY_PRICE = 'percentage_of_base_delivery_price',
  PERCENTAGE_OF_FULL_DELIVERY_PRICE = 'percentage_of_full_delivery_price',
  PERCENTAGE_OF_FOOD_PRICE = 'percentage_of_food_price',
  PERCENTAGE_OF_FOOD_PRICE_WITH_DISCOUNT_CAP = 'percentage_of_food_price_with_discount_cap',
}

export enum PromotionSectionKeys {
  TYPE_OF_DISCOUNT = 'type_of_discount',
  DISCOUNT_VALUE_PERCENTAGE = 'discount_value_percentage',
  DISCOUNT_TARGETING = 'discount_targeting',
  MINIMUM_ORDER_VALUE = 'minimum_order_value',
  MAXIMUM_DISCOUNT_VALUE = 'maximum_discount_value',
  DOUBLE_DEAL_VALUE = 'double_deal_value',
}

export enum PromotionFilterKeys {
  TYPE_OF_DISCOUNT_FREE_DELIVERY = 'type_of_discount_free_delivery',
  TYPE_OF_DISCOUNT_FREE_BASE_DELIVERY = 'type_of_discount_free_base_delivery',
  TYPE_OF_DISCOUNT_FULL_MENU = 'type_of_discount_full_menu',
  TYPE_OF_DISCOUNT_SELECTED_ITEMS = 'type_of_discount_selected_items',
  TYPE_OF_DISCOUNT_DOUBLE_DEAL = 'type_of_discount_double_deal',
  TYPE_OF_DISCOUNT_DOUBLE_DEAL_v2 = 'type_of_discount_double_deal_v2',
  DISCOUNT_TARGETING_ALL_CUSTOMERS = 'discount_targeting_all_customers',
  DISCOUNT_TARGETING_NEW_CUSTOMERS = 'discount_targeting_new_customers',
  DISCOUNT_TARGETING_RETURNING_CUSTOMERS = 'discount_targeting_returning_customers',
  DISCOUNT_TARGETING_BEST_CUSTOMERS = 'discount_targeting_best_customers',
  DISCOUNT_TARGETING_CHURNED_CUSTOMERS = 'discount_targeting_churned_customers',
  DISCOUNT_VALUE_PERCENTAGE = 'discount_value_percentage',
  MAXIMUM_DISCOUNT_VALUE = 'maximum_discount_value',
  MINIMUM_ORDER_VALUE = 'minimum_order_value',
  SELECT_DISABLED = 'select_disabled',
  DOUBLE_DEAL_VALUE = 'double_deal_value',
}

export enum CreatePromotionTemplateInputType {
  SELECT = 'select',
  DROPDOWN = 'dropdown',
  FINAL = 'final',
  SECTION = 'section',
  SELECT_DISABLED = 'select_disabled',
}

export interface PromotionCopyPrefillParams {
  preselect_by_trait?: string;
  from_campaign_log_id?: number;
}

export type Choice =
  | SectionChoice<Section>
  | DropDownChoice<Section>
  | FinalChoice
  | DisabledSelectChoice;

interface ChoiceBase {
  type: CreatePromotionTemplateInputType;
  value: string;
}

interface SectionChoice<T> extends ChoiceBase {
  type: CreatePromotionTemplateInputType.SECTION;
  section: T;
  key: PromotionFilterKeys;
}

interface DropDownChoice<T> extends ChoiceBase {
  type: CreatePromotionTemplateInputType.DROPDOWN;
  section: T;
  key: PromotionFilterKeys;
}

export interface DoubleDealValue {
  menu_percentage?: number;
  delivery_max_value?: number;
}

export interface FinalChoice extends ChoiceBase {
  title?: LocalisedString;
  type: CreatePromotionTemplateInputType.FINAL;
  key?: PromotionFilterKeys;
  trait: string;
  start_date: number;
  end_date: number;
  campaign_bonus_type: DeliveryBonusType;
  selected_items_discount_tag?: string;
  is_bolt_plus?: boolean;
  description?: LocalisedString;
  double_deal_value?: DoubleDealValue;
}

export interface DisabledSelectChoice extends ChoiceBase {
  key: PromotionFilterKeys;
  type: CreatePromotionTemplateInputType.SELECT_DISABLED;
}

export interface GetWeeklyForecastRequest {
  provider_id: number;
  filters: {
    [label: string]: GetWeeklyForecastRequestFilters;
  };
}

export interface WeeklyForecast {
  discount_percentage: number;
  required_minimum_order_value: number;
  expected_weekly_spend: number;
  expected_weekly_new_users: number | undefined;
  expected_weekly_additional_gmv: number;
  currency: string;
}

export enum Compare {
  LT = 'lt',
  GT = 'gt',
  LE = 'le',
  GE = 'ge',
  EQ = 'eq',
}

export interface GetProviderEligibilityDataRequest {
  provider_id: number;
}

export interface GetProvidersEligibilityDataResponse {
  is_menu_inflation: boolean;
  banned_until?: number;
  is_metrics_satisfied: boolean;
  metrics_comparison_result?: {
    [name: string]: {
      value: number;
      operator: Compare;
      target: number;
    };
  };
}

export interface PromotionWeeklyForecastResult {
  [label: string]: WeeklyForecast;
}

export interface GetWeeklyForecastResponse {
  result: PromotionWeeklyForecastResult;
}

export interface SignUpPromotionRequest {
  provider_id: number;
  trait_slug: string;
  start_timestamp: number; // unix timestamp
  end_timestamp: number; // unix timestamp
  campaign_tag?: string;
  menu_items_info?: MenuItemInfo[];
}

export interface SignUpPromotionResponse {
  promotion_id: number;
}

export interface CancelPromotionRegistrationRequest {
  provider_id: number;
  promotion_id: number;
}

export interface GetAssignedPromotionsRequest {
  provider_id: number;
}

export interface GetAssignedPromotionsResponse {
  campaigns: SelfServicePromotionDetailsResponse[];
}

export interface SelfServiceCampaignResponse {
  id: number;
  title: LocalisedString;
  description: LocalisedString;
  campaign_bonus_type?: DeliveryBonusType | null;
  trait_slug: string;
  start_timestamp: number;
  end_timestamp: number;
  action: SelfServiceCampaignAction;
  orders: number;
  customers: number;
  net_orders_value: number;
  total_spend: number;
  currency: string;
  last_update: number;
  new_users: number;
  gmv_impact: number;
  banned_until: number | null;
  selected_items_config: SelectedItemsCityConfig;
  city_exclusion_tags: string[];
}

export interface SelfServicePromotionDetailsResponse extends SelfServiceCampaignResponse {
  is_best_user_campaign?: boolean;
  best_user_promotion_permissions: BestUserPromotionPermissions;
}

export enum SelfServiceCampaignAction {
  ASSIGNED = 'assigned',
  REMOVED = 'removed',
  FOOD_BANNED = 'food_banned',
}

export interface GetPromotionDetailsRequest {
  provider_id: number;
  promotion_id: number;
}

export interface GetPromotionDetailsResponse {
  campaign: SelfServicePromotionDetailsResponse | null;
}

export interface GetPromotionsPermissionsRequest {
  provider_id: number;
}

export interface GetSelfServiceCampaignMetaRequest {
  self_service_campaign_log_id: number;
}

export interface GetSelfServiceCampaignMetaResponse {
  result: {
    menu_items_info: MenuItemInfo[];
    special_tag: string;
  };
}

export interface MenuItemInfo {
  product_id?: string;
  global_stable_context_id: string;
  name: TranslatableField;
}

export interface MenuItemsUpdateInfo {
  global_stable_context_id: string;
  product_id?: string;
  name: TranslatableField;
  price: number;
  currency: string;
}

export interface ProviderPromotionsPermissions {
  can_view_selected_items_menu_editor: boolean;
  net_revenue_uplift?: number;
  should_skip_landing_page: boolean;
}

export interface GetPromotionsPermissionsResponse {
  result: ProviderPromotionsPermissions;
}

export interface UpdateMenuItemsForPromotionRequest {
  provider_id: number;
  city_id: number;
  self_service_campaign_log_id: number;
  menu_items_info: MenuItemInfo[];
  campaign_tag: string;
}

export interface SelfServiceCampaignTags {
  menu_items_info: MenuItemInfo[];
  campaign_tag: string;
}

export interface UpdateMenuItemsForPromotionResponse {
  result: SelfServiceCampaignTags;
}

export interface GetCityExclusionTagsRequest {
  city_id: number;
}

export interface CityExclusionTagsResponse {
  result: string[];
}

export interface SmartPromotionTranslations {
  title: {
    [lang: string]: { text: string };
  };
  subtitle: {
    [lang: string]: { text: string };
  };
  tooltip: {
    [lang: string]: { text: string };
  };
}

export interface SmartPromotion {
  offer_id: number;
  offer_stats?: StatsData;
  start: number;
  end: number;
  currency_symbol: string;
  can_boost_visibility: boolean;
  priority: number;
  icon: string;
  translations: SmartPromotionTranslations;
  type: OfferType;
}

interface StatsData {
  smart_forecastd_uplift_per_week: number;
  regular_forecastd_uplift_per_week: number;
  net_uplift_per_day: number;
  net_uplift_per_week: number;
  estimated_cost_per_week: number;
  visibility_boost: number;
}

export interface GetSmartPromotionOfferRequest {
  city_id: number;
  country: string;
  provider_id: number;
}

export interface GetSmartPromotionOfferResponse {
  regular_promotions_uplift_percentage: number;
  net_revenue_uplift: number;
  promotions: SmartPromotion[];
  default_promotions_mode: OfferMode;
}

export interface GetSmartPromotionsHistoryRequest {
  provider_id: number;
}

export interface GetSmartPromotionsHistoryResponse {
  result: PromotionPerformanceLog[];
}

export enum SmartPromotionState {
  ACTIVE = 'active',
  DISABLED = 'disabled',
}

export interface PromotionPerformanceLog {
  id: number;
  promotion_type: string;
  start: number;
  end: number;
  spendings: number;
  forecasted_uplift: number;
  factual_uplift: number;
  state: SmartPromotionState;
  currency_symbol: string;
}

export interface WeeklySchedule {
  weekday: number;
  start_at_minutes_from_midnight: number;
  stop_at_minutes_from_midnight: number;
}

export interface StartEndWithSchedule {
  start: number;
  end: number;
  weekly_schedule: WeeklySchedule[] | undefined;
}

interface SmartPromotionSignUpData {
  offer_id: number;
  provider_id: number;
  code: number;
}

export enum OfferMode {
  BLACKBOX = 'black_box',
  WHITEBOX = 'white_box',
}

export interface SignUpSmartPromotionRequest {
  provider_id: number;
  offer_ids: number[];
  schedule: StartEndWithSchedule;
  offer_mode: OfferMode;
  maximize_net_profit_value?: number;
}

export interface SmartPromotionSignUpResponse {
  result: SmartPromotionSignUpData[];
}

export interface GetConflictingSelfServicePromotionsRequest {
  provider_id: number;
  start_time?: number;
  end_time?: number;
}

export interface CancelSelfServicePromotionsRequest {
  provider_id: number;
}

export interface GetConflictingSelfServicePromotionsResponse {
  result: ActiveSelfServiceCampaign[];
}

export interface ActiveSelfServiceCampaign {
  id: number;
  start_timestamp: number;
  end_timestamp: number;
  title: LocalisedString;
}

export interface CancelSmartPromotionsRequest {
  provider_id: number;
  smart_promotion_log_id: number;
}

export interface GetSmartPromotionDetailRequest {
  provider_id: number;
  smart_promotion_log_id: number;
}

enum PromotionType {
  SMART_PROMOTION = 'smart_promotion',
  SELECTED_ITEM_DISCOUNT = 'selected_item_discount',
}

export enum OfferType {
  TOP_UP = 'topup',
  REGULAR = 'regular',
  DOUBLE_DEAL = 'double_deal',
  LUNCH = 'lunch',
}

interface EstimatedCostPerOffer {
  end?: number;
  estimated_cost_per_week: number;
  type: OfferType;
  translation: SmartPromotionTranslations;
}

export interface SmartPromotionDetailData {
  id: number;
  provider_id: number;
  promotion_type: PromotionType;
  state: SmartPromotionState;
  start: number;
  end: number;
  schedule: StartEndWithSchedule;
  statistics: {
    spendings: number;
    factual_uplift: number;
    forecasted_uplift: number;
  };
  offer_mode: OfferMode;
  offers: {
    [offer_id: number]: EstimatedCostPerOffer;
  };
  currency_symbol: string;
}

export interface GetSmartPromotionDetailResponse {
  result: SmartPromotionDetailData;
}

export class CampaignDeliveryProvider extends Gateway {
  public getAvailableRecommendationsAndFocuses = async (
    request: GetAvailableRecommendationsAndFocusesRequest,
  ): Promise<GetAvailableRecommendationsAndFocusesResponse> => {
    return this.postJSON<
      GetAvailableRecommendationsAndFocusesRequest,
      Promise<GetAvailableRecommendationsAndFocusesResponse>
    >('/deliveryProviderPortal/getRecommendationsAndFocuses', request);
  };

  public getTestFlags = async (request: GetTestFlagsRequest): Promise<GetTestFlagsResponse> => {
    return this.postJSON<GetTestFlagsRequest, GetTestFlagsResponse>(
      '/deliveryProviderPortal/getTestFlags',
      { provider_id: request.provider_id },
    );
  };

  public storeAnalyticsEvent = async (request: StoreAnalyticsEventRequest): Promise<void> => {
    return this.postJSON<StoreAnalyticsEventRequest, void>(
      '/deliveryProviderPortal/storeAnalyticsEvent',
      request,
    );
  };

  public getProviderAvailablePromotions = async (
    request: GetProviderAvailablePromotionsRequest,
  ): Promise<GetProviderAvailablePromotionsResponse> => {
    return this.postJSON<
      GetProviderAvailablePromotionsRequest,
      Promise<GetProviderAvailablePromotionsResponse>
    >('/deliveryProviderPortal/getProviderAvailablePromotions', request);
  };

  public getAvailablePromotionsOptionsTree = async (
    request: GetAvailablePromotionsOptionsTreeRequest,
  ): Promise<GetAvailablePromotionsOptionsTreeResponse> => {
    return this.postJSON<
      GetAvailablePromotionsOptionsTreeRequest,
      Promise<GetAvailablePromotionsOptionsTreeResponse>
    >('/deliveryProviderPortal/getAvailablePromotionsOptionsTree', request);
  };

  public getWeeklyForecast = async (
    request: GetWeeklyForecastRequest,
  ): Promise<GetWeeklyForecastResponse> => {
    return this.postJSON<GetWeeklyForecastRequest, Promise<GetWeeklyForecastResponse>>(
      '/deliveryProviderPortal/getWeeklyForecast',
      request,
    );
  };

  public signUpPromotion = async (
    request: SignUpPromotionRequest,
  ): Promise<SignUpPromotionResponse> => {
    return this.postJSON<SignUpPromotionRequest, Promise<SignUpPromotionResponse>>(
      '/deliveryProviderPortal/signUpPromotion',
      request,
    );
  };

  public cancelPromotionRegistration = async (
    request: CancelPromotionRegistrationRequest,
  ): Promise<void> => {
    return this.postJSON<CancelPromotionRegistrationRequest, Promise<void>>(
      '/deliveryProviderPortal/cancelPromotionRegistration',
      request,
    );
  };

  public getAssignedPromotions = async (
    request: GetAssignedPromotionsRequest,
  ): Promise<GetAssignedPromotionsResponse> => {
    return this.postJSON<GetAssignedPromotionsRequest, Promise<GetAssignedPromotionsResponse>>(
      '/deliveryProviderPortal/getAssignedPromotions',
      request,
    );
  };

  public getProviderEligibilityData = async (
    request: GetProviderEligibilityDataRequest,
  ): Promise<GetProvidersEligibilityDataResponse> => {
    return this.postJSON<
      GetProviderEligibilityDataRequest,
      Promise<GetProvidersEligibilityDataResponse>
    >('/deliveryProviderPortal/getProviderEligibilityData', request);
  };

  public getPromotionDetails = async (
    request: GetPromotionDetailsRequest,
  ): Promise<GetPromotionDetailsResponse> => {
    return this.postJSON<GetPromotionDetailsRequest, Promise<GetPromotionDetailsResponse>>(
      '/deliveryProviderPortal/getPromotionDetails',
      request,
    );
  };

  public getPromotionsPermissions = async (
    request: GetPromotionsPermissionsRequest,
  ): Promise<GetPromotionsPermissionsResponse> => {
    return this.postJSON<
      GetPromotionsPermissionsRequest,
      Promise<GetPromotionsPermissionsResponse>
    >('/deliveryProviderPortal/getPromotionsPermissions', request);
  };

  public getSelfServiceCampaignMeta = async (
    request: GetSelfServiceCampaignMetaRequest,
  ): Promise<GetSelfServiceCampaignMetaResponse> => {
    return this.postJSON<
      GetSelfServiceCampaignMetaRequest,
      Promise<GetSelfServiceCampaignMetaResponse>
    >('/deliveryProviderPortal/getSelfServiceCampaignMeta', request);
  };

  public updateMenuItemsForPromotion = async (
    request: UpdateMenuItemsForPromotionRequest,
  ): Promise<UpdateMenuItemsForPromotionResponse> => {
    return this.postJSON<
      UpdateMenuItemsForPromotionRequest,
      Promise<UpdateMenuItemsForPromotionResponse>
    >('/deliveryProviderPortal/updateMenuItemsForPromotion', request);
  };

  public getCityExclusionTags = async (
    request: GetCityExclusionTagsRequest,
  ): Promise<CityExclusionTagsResponse> => {
    return this.postJSON<GetCityExclusionTagsRequest, Promise<CityExclusionTagsResponse>>(
      '/deliveryProviderPortal/getCityExclusionTags',
      request,
    );
  };

  public getSmartOffers = async (
    request: GetSmartPromotionOfferRequest,
  ): Promise<GetSmartPromotionOfferResponse> => {
    return this.postJSON<GetSmartPromotionOfferRequest, Promise<GetSmartPromotionOfferResponse>>(
      '/deliveryProviderPortal/getSmartOffers',
      request,
    );
  };

  public getSmartPromotionsHistory = async (
    request: GetSmartPromotionsHistoryRequest,
  ): Promise<GetSmartPromotionsHistoryResponse> => {
    return this.postJSON<
      GetSmartPromotionsHistoryRequest,
      Promise<GetSmartPromotionsHistoryResponse>
    >('/deliveryProviderPortal/getSmartPromotionLogs', request);
  };

  public signUpSmartOffers = async (
    request: SignUpSmartPromotionRequest,
  ): Promise<SmartPromotionSignUpResponse> => {
    return this.postJSON<SignUpSmartPromotionRequest, Promise<SmartPromotionSignUpResponse>>(
      '/deliveryProviderPortal/signUpSmartPromotion',
      request,
    );
  };

  public cancelSelfServicePromotions = async (
    request: CancelSelfServicePromotionsRequest,
  ): Promise<void> => {
    return this.postJSON<CancelSelfServicePromotionsRequest, Promise<void>>(
      '/deliveryProviderPortal/cancelSelfServicePromotions',
      request,
    );
  };

  public getConflictingSelfServicePromotions = async (
    request: GetConflictingSelfServicePromotionsRequest,
  ): Promise<GetConflictingSelfServicePromotionsResponse> => {
    return this.postJSON<
      GetConflictingSelfServicePromotionsRequest,
      Promise<GetConflictingSelfServicePromotionsResponse>
    >('/deliveryProviderPortal/getConflictingSelfServicePromotions', request);
  };

  public cancelSmartPromotions = async (request: CancelSmartPromotionsRequest): Promise<void> => {
    return this.postJSON<CancelSmartPromotionsRequest, Promise<void>>(
      '/deliveryProviderPortal/cancelSmartPromotions',
      request,
    );
  };

  public getSmartPromotionDetails = async (
    request: GetSmartPromotionDetailRequest,
  ): Promise<GetSmartPromotionDetailResponse> => {
    return this.postJSON<GetSmartPromotionDetailRequest, GetSmartPromotionDetailResponse>(
      '/deliveryProviderPortal/getSmartPromotionDetails',
      request,
    );
  };
}
